import React, { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  IconLayoutDashboard,
  IconCalendarEvent,
  IconCashRegister,
  IconUsers,
  IconDental,
  IconStethoscope,
  IconReceipt,
  IconSettings,
  IconLogout,
  IconMenu2,
} from "@tabler/icons-react";
import "./app-layout.scss";
import { useClinicData } from "../../context/ClinicDataContext";
import { formatDateLong } from "../../utils/format";
import { FEATURES } from "../../config/features";

export const BASE_PATH = "/admin";

const menuItems = [
  { label: "Dashboard", icon: IconLayoutDashboard, path: BASE_PATH },
  { label: "Appointments", icon: IconCalendarEvent, path: `${BASE_PATH}/appointments` },
  { label: "Checkout", icon: IconCashRegister, path: `${BASE_PATH}/checkout` },
  { label: "Patients", icon: IconUsers, path: `${BASE_PATH}/patients` },
  { label: "Treatments", icon: IconDental, path: `${BASE_PATH}/treatments` },
  { label: "Dentists", icon: IconStethoscope, path: `${BASE_PATH}/dentists` },
  { label: "Billing", icon: IconReceipt, path: `${BASE_PATH}/billing`, comingSoon: !FEATURES.billing },
  { label: "Settings", icon: IconSettings, path: `${BASE_PATH}/settings` },
];

const titleFor = (pathname) => {
  if (pathname.startsWith(`${BASE_PATH}/patients/`)) return "Patient record";
  const match = [...menuItems]
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`));
  return match ? match.label : "Dashboard";
};

const AppLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { clinic, today } = useClinicData();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  return (
    <div className="app-layout">
      <aside className={`app-layout__sidebar ${sidebarOpen ? "app-layout__sidebar--open" : ""}`}>
        <div className="app-layout__brand">
          <span className={`app-layout__brand-mark ${clinic.logoUrl ? "app-layout__brand-mark--logo" : ""}`}>
            {clinic.logoUrl ? <img src={clinic.logoUrl} alt="" /> : <IconDental size={22} stroke={1.75} />}
          </span>
          <span className="app-layout__brand-text">
            <strong>{clinic.name}</strong>
            <span>Dental admin</span>
          </span>
        </div>

        <nav className="app-layout__menu" aria-label="Main">
          {menuItems.map(({ label, icon: Icon, path, comingSoon }) =>
            comingSoon ? (
              <span key={path} className="app-layout__menu-item app-layout__menu-item--disabled" aria-disabled="true">
                <Icon className="app-layout__menu-icon" size={20} stroke={1.75} />
                <span>{label}</span>
                <span className="app-layout__soon">Coming soon</span>
              </span>
            ) : (
              <NavLink
                key={path}
                to={path}
                end={path === BASE_PATH}
                className={({ isActive }) => `app-layout__menu-item ${isActive ? "app-layout__menu-item--active" : ""}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="app-layout__menu-icon" size={20} stroke={1.75} />
                <span>{label}</span>
              </NavLink>
            )
          )}
        </nav>

        <button className="app-layout__logout" type="button" onClick={handleLogout}>
          <IconLogout size={20} stroke={1.75} />
          Log out
        </button>
      </aside>

      {sidebarOpen && (
        <button
          className="app-layout__overlay"
          type="button"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="app-layout__main">
        <header className="app-layout__topbar">
          <button
            className="app-layout__menu-toggle"
            type="button"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          >
            <IconMenu2 size={22} stroke={1.75} />
          </button>

          <div className="app-layout__heading">
            <h1 className="app-layout__title">{titleFor(pathname)}</h1>
            <p className="app-layout__date">{formatDateLong(today)}</p>
          </div>

          <div className="app-layout__profile">
            <span className="app-layout__avatar">FD</span>
            <span className="app-layout__profile-text">
              <strong>Front desk</strong>
              <span>Receptionist</span>
            </span>
          </div>
        </header>

        <section className="app-layout__content">
          <Outlet />
        </section>
      </main>
    </div>
  );
};

export default AppLayout;
